/**
 * A hash of the binary layout itself.
 *
 * `PROTOCOL_VERSION` and `SIM_HASH` are hand-maintained, and on 2026-09-17 a
 * client bundle built before the per-part HP field grew from u8 to u16 was
 * deployed with a server that had grown it: both hashes still matched, the
 * client dropped every snapshot as malformed and the match looked frozen while
 * control messages kept arriving. So the layout signs itself: this module
 * encodes a fixture built from constants (never from the tuning table, which
 * `SIM_HASH` already covers) and hashes the bytes. Add, widen or reorder any
 * field and the hash moves, so the join is refused with a plain reason.
 *
 * The fixture is a complete `MatchState` literal on purpose: a new field makes
 * the type checker stop here, which is the moment to think about the wire.
 */
import type { MatchState, StrikeState } from "@meleeroyale/sim";
import { encodeInputs } from "./input.ts";
import { encodeSnapshot } from "./snapshot.ts";

function fixtureStrike(limb: 0 | 1 | 2 | 3): StrikeState {
  return {
    limb,
    arc: 1,
    phase: "throw",
    ticks: 3,
    charge: 0.5,
    windupTicks: 2,
    throwTicks: 6,
    recoveryTicks: 8,
    origin: { x: 0.11, y: 1.2, z: 0.3 },
    control: { x: 0.22, y: 1.3, z: 0.6 },
    aim: { x: 0.33, y: 1.4, z: 0.9 },
    aimWorld: { x: 1.44, y: 1.5, z: 2.1 },
    rest: { x: 0.36, y: 1.15, z: 0.25 },
    end: { x: 0.4, y: 1.45, z: 0.95 },
    tipPrev: { x: 1.4, y: 1.5, z: 2.0 },
    progress: 0.75,
    pathLength: 0.85,
    lead: -0.5,
    hit: true,
    blocked: true,
    whiffed: false,
  };
}

function fixtureState(): MatchState {
  return {
    config: { sessionSize: 3, seed: 7 },
    phase: "fight",
    tick: 1234,
    fightStartTick: 300,
    clockTicks: 5678,
    cage: { r: 7.25, targetR: 6.5, phase: 2, nextShrinkTick: 1350, warning: true },
    fighters: [0, 1].map((id) => ({
      id,
      nickname: `f${id}`,
      isBot: id === 1,
      build: { speed: 1, strength: 1 },
      alive: true,
      pos: { x: 1.5 + id, y: 0, z: -2.25 },
      vel: { x: 0.5, y: 0, z: 1.5 },
      impulse: { x: 0.125, y: 0, z: -0.25 },
      facing: 1.25,
      lookYaw: 12345,
      lookPitch: -20,
      hp: [90, 150, 55, 60, 40, 45, 75, 80, 50, 55],
      stamina: 66,
      exhausted: false,
      guard: true,
      duck: 0.5,
      lean: -0.5,
      stance: -0.5,
      southpaw: true,
      twist: 1.25,
      checking: id === 1,
      coverZone: 1 as const,
      step: { ticksLeft: 4, dir: { x: 0.6, y: 0, z: 0.8 } },
      handL: { x: -0.35, y: 1.3, z: 0.25 },
      handR: { x: 0.35, y: 1.3, z: 0.25 },
      footL: { x: -0.12, y: 0, z: 0 },
      footR: { x: 0.12, y: 0, z: 0 },
      chargeArm: { limb: 1 as const, arc: 2 as const, zone: 1 as const, ticks: 5 },
      chargeLeg: null,
      strikeArm: fixtureStrike(0),
      strikeLeg: fixtureStrike(3),
      queue: [
        { limb: 0 as const, arc: 1 as const, zone: 1 as const },
        { limb: 3 as const, arc: 0 as const, zone: 2 as const },
      ],
      chain: 1,
      parry: { arm: 1 as const, ticksLeft: 3, total: 6 },
      stagger: 2,
      initiativeLock: 4,
      crushTicks: 1,
      damageDealt: 123,
      lastHitBy: 1,
      lastInput: {
        tick: 1233,
        moveX: 100,
        moveY: -60,
        yaw: 12345,
        pitch: -20,
        buttons: 0b101,
        viewTick: 1227,
      },
      prevButtons: 0b11,
      past: [{ tick: 1233, x: 1.5, z: -2.25, facing: 1.25 }],
    })),
    rng: { state: 999 },
    winner: -1,
    endReason: "none",
  };
}

/** Every byte a client and a server must agree on, in one string. */
export const WIRE_HASH = computeWireHash();

function computeWireHash(): string {
  const state = fixtureState();
  const snapshot = encodeSnapshot(state, 4321, 0);
  const inputs = encodeInputs([state.fighters[0]?.lastInput ?? emptyish()]);
  let h = 0x811c9dc5;
  for (const bytes of [snapshot, inputs]) {
    h = mix(h, bytes.byteLength);
    for (const b of bytes) h = mix(h, b);
  }
  return `w${(h >>> 0).toString(16)}`;
}

function emptyish() {
  return { tick: 0, moveX: 0, moveY: 0, yaw: 0, pitch: 0, buttons: 0, viewTick: 0 };
}

function mix(h: number, byte: number): number {
  return Math.imul((h ^ (byte & 0xff)) >>> 0, 0x01000193) >>> 0;
}
